import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useDeleteFiles, useRetryFiles } from "@/hooks/use-knowledge-base";
import { failureMessage, type KnowledgeBaseFile } from "@/services/knowledge-base.service";
import { BULK_MAX, BULK_TOO_MANY, fileCount, formatCount, retryRefusals } from "@/utils/kb-upload-rules";

interface FileBulkBarProps {
  storeId: string;
  workspaceId: string;
  /** The knowledge base's files. */
  files: KnowledgeBaseFile[];
  /** The ticked ones; the bar shows while there is one. */
  ticked: KnowledgeBaseFile[];
  /** Owner, admin: Delete. */
  canDelete: boolean;
  /** The plan's files per knowledge base, once the usage has loaded: a retry over it is refused here. */
  maxFiles: number | undefined;
  /** A batch went through: nothing is ticked any more. */
  onDone: () => void;
  /** Files are gone: the description is rewritten from the others. */
  onDeleted: () => void;
}

const TRY_AGAIN = "Try again in a moment.";

/**
 * What is done to the ticked files at once (step 3 §5.5): Retry of the failed ones, each checked first as
 * `kb_confirm_upload` checks it, and Delete of all of them (owner, admin), asked first. Up to 25 at a time,
 * as the function takes them.
 */
export function FileBulkBar({
  storeId,
  workspaceId,
  files,
  ticked,
  canDelete,
  maxFiles,
  onDone,
  onDeleted,
}: FileBulkBarProps) {
  const retryMany = useRetryFiles(storeId, workspaceId);
  const removeMany = useDeleteFiles(storeId, workspaceId);
  const { toast } = useToast();
  /** The ids a Delete asks about, as they were ticked when it was pressed; empty while it does not. */
  const [deleting, setDeleting] = useState<string[]>([]);
  const [deleteError, setDeleteError] = useState<string>();
  const failed = ticked.filter((file) => file.status === "error");
  const tooMany = ticked.length > BULK_MAX;
  const busy = retryMany.isPending || removeMany.isPending;

  // Each refusal names its file: every sentence says "this file".
  const nameOf = new Map(failed.map((file) => [file.id, file.display_name]));
  const named = (id: string, message: string) => `${nameOf.get(id)}: ${message}`;

  const retried = (count: number, refusals: string[]) => {
    toast({
      variant: count === 0 ? "destructive" : undefined,
      title: `${fileCount(count)} will be processed again.`,
      description: refusals.join(" ") || undefined,
    });
    onDone();
  };

  // A file refused here is not sent; with none left, nothing is.
  const retry = () => {
    const refusals = maxFiles === undefined ? [] : retryRefusals(failed, files, maxFiles);
    const refusedHere = failed.flatMap((file, i) => (refusals[i] ? [named(file.id, refusals[i])] : []));
    const ids = failed.filter((_, i) => !refusals[i]).map((file) => file.id);
    if (ids.length === 0) return retried(0, refusedHere);
    retryMany.mutate(ids, {
      onSuccess: ({ files: queued, refused }) =>
        retried(queued.length, [...refusedHere, ...refused.map((refusal) => named(refusal.id, refusal.message))]),
      onError: (error) =>
        toast({
          variant: "destructive",
          title: "The files were not queued again",
          description: failureMessage(error, TRY_AGAIN),
        }),
    });
  };

  const confirmDelete = async () => {
    setDeleteError(undefined);
    try {
      const { deleted } = await removeMany.mutateAsync(deleting);
      toast({ title: `${fileCount(deleted.length)} deleted.` });
      setDeleting([]);
      onDone();
      onDeleted();
    } catch (error) {
      setDeleteError(failureMessage(error, `The files could not be deleted. ${TRY_AGAIN}`));
    }
  };

  return (
    <>
      {ticked.length > 0 && (
        <div
          role="group"
          aria-label="Selected files"
          className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/30 px-4 py-2 text-sm"
        >
          <span className="font-medium">{formatCount(ticked.length)} selected</span>
          {tooMany && <span className="text-destructive">{BULK_TOO_MANY}</span>}
          <div className="ml-auto flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={tooMany || busy || failed.length === 0}
              aria-busy={retryMany.isPending}
              onClick={retry}
            >
              {retryMany.isPending && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
              {retryMany.isPending ? "Retrying…" : "Retry"}
            </Button>
            {canDelete && (
              <Button
                size="sm"
                variant="destructive"
                disabled={tooMany || busy}
                onClick={() => {
                  setDeleteError(undefined);
                  setDeleting(ticked.map((file) => file.id));
                }}
              >
                Delete
              </Button>
            )}
          </div>
        </div>
      )}

      <AlertDialog open={deleting.length > 0} onOpenChange={() => !removeMany.isPending && setDeleting([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {fileCount(deleting.length)}?</AlertDialogTitle>
            <AlertDialogDescription>
              They are removed from this knowledge base, and your agents can no longer search them. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && (
            <p role="alert" className="text-sm text-destructive">
              {deleteError}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeMany.isPending}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={removeMany.isPending}
              aria-busy={removeMany.isPending}
            >
              {removeMany.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {removeMany.isPending ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
